import type { ReactNode } from 'react';
import {
  isSectionVisibleToAudience,
  PASSPORT_SECTION_AUDIENCE,
  type PassportAudience,
} from '@/lib/passport/sectionAudience';

interface PassportSectionProps {
  /**
   * The section's id in `PASSPORT_SECTION_AUDIENCE`. An id with no declaration
   * renders nothing — that is the contract, not a bug.
   */
  id: string;
  /** The audiences the current viewer holds, from `resolveViewerAudiences`. */
  audiences: ReadonlySet<PassportAudience>;
  children: ReactNode;
}

/**
 * CORE-C GAP-004 — the composition boundary where a Passport section's
 * audience is enforced.
 *
 * WHY A WRAPPER AND NOT A PROP. The defect this replaces was not that anyone
 * disagreed about who should see Diagnostic Competency; it was that the answer
 * lived in an optional prop, `isSelfView`, which `PassportHome` passed and
 * `TechnicianPassport` did not. Correctness depended on each developer
 * remembering. Here the audience decision is made in one place from one table,
 * and the failure mode of forgetting is that a section does not render rather
 * than that it renders to everyone.
 *
 * WHAT IT DOES NOT DO. It does not fetch, and it does not stop a fetch: the
 * children are simply not mounted, so their queries never start. That is a
 * presentation outcome, not an authorization one. The database decides what
 * this browser may hold; this decides what this composition draws. Those are
 * different questions with different owners, and a test written against this
 * boundary proves only the second.
 *
 * A section may keep its own check as well — `PassportDiagnosticCompetency`
 * does, and `ToolboxSection` gates its private inventory internally. Defence in
 * depth is welcome. Correctness no longer rests on it.
 */
export function PassportSection({ id, audiences, children }: PassportSectionProps) {
  if (!isSectionVisibleToAudience(id, audiences)) {
    if (import.meta.env?.DEV && !PASSPORT_SECTION_AUDIENCE[id]) {
      // Loud in development, silent in production: a section that reaches a
      // user's browser undeclared should disappear, not explain itself to them.
      console.warn(
        `[CORE-C] Passport section "${id}" has no audience declaration in ` +
          'PASSPORT_SECTION_AUDIENCE and will not render. Declare its audiences ' +
          'in src/lib/passport/sectionAudience.ts.',
      );
    }
    return null;
  }

  return <>{children}</>;
}
